import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Chip,
	Tooltip,
	TextField,
} from "@mui/material";
import { mdiPencil, mdiRefresh, mdiMagnify, mdiClose, mdiRobotOutline, mdiCheckOutline, mdiTrashCanOutline } from "@mdi/js";
import { clsx } from "@ismael1361/utils";
import { HandleError } from "shared/utils";
import { NotistackServices } from "shared/services";
import { MemberLibraryServices, PublicMemberLibraryDTO } from "shared/services/MemberLibraryServices";

import styles from "./styles.module.scss";

const formatTokens = (n: number): string => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".0", "")}M`;
	if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
	return String(n);
};

const SENIORITY_LEVELS = ["junior", "pleno", "senior"] as const;
type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

interface SeniorityLevelConfig {
	token_limit: number;
	rate_limit_per_minute: number;
}

interface SeniorityFormState {
	junior: SeniorityLevelConfig;
	pleno: SeniorityLevelConfig;
	senior: SeniorityLevelConfig;
}

const defaultSeniority = (config: Record<string, any>): SeniorityFormState => ({
	junior: {
		token_limit: config?.junior?.token_limit ?? 100_000,
		rate_limit_per_minute: config?.junior?.rate_limit_per_minute ?? 60,
	},
	pleno: {
		token_limit: config?.pleno?.token_limit ?? 300_000,
		rate_limit_per_minute: config?.pleno?.rate_limit_per_minute ?? 120,
	},
	senior: {
		token_limit: config?.senior?.token_limit ?? 1_000_000,
		rate_limit_per_minute: config?.senior?.rate_limit_per_minute ?? 240,
	},
});

export const MemberLibraryPage: React.FC = () => {
	const [templates, setTemplates] = useState<PublicMemberLibraryDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [editTarget, setEditTarget] = useState<PublicMemberLibraryDTO | null>(null);
	const [seniorityForm, setSeniorityForm] = useState<SeniorityFormState>({
		junior: { token_limit: 0, rate_limit_per_minute: 60 },
		pleno: { token_limit: 0, rate_limit_per_minute: 120 },
		senior: { token_limit: 0, rate_limit_per_minute: 240 },
	});
	const [confirmToggle, setConfirmToggle] = useState<PublicMemberLibraryDTO | null>(null);
	const time = useRef<NodeJS.Timeout>(undefined);

	const filtered = React.useMemo(() => {
		if (!searchQuery) return templates;
		const q = searchQuery.toLowerCase();
		return templates.filter((t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q));
	}, [templates, searchQuery]);

	const load = useCallback(() => {
		setLoading(true);
		clearTimeout(time.current);
		time.current = setTimeout(async () => {
			try {
				const data = await MemberLibraryServices.listAll();
				setTemplates(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar templates");
			} finally {
				setLoading(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const openEdit = (t: PublicMemberLibraryDTO) => {
		setEditTarget(t);
		setSeniorityForm(defaultSeniority(t.seniorityConfig));
	};

	const handleSaveSeniority = async () => {
		if (!editTarget) return;
		setSaving(true);
		try {
			await MemberLibraryServices.update(editTarget.id, {
				seniority_config: {
					junior: seniorityForm.junior,
					pleno: seniorityForm.pleno,
					senior: seniorityForm.senior,
				},
			});
			NotistackServices.showSuccessMessage("Limites de tokens atualizados!");
			setEditTarget(null);
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao salvar.");
		} finally {
			setSaving(false);
		}
	};

	const handleToggleActive = async () => {
		if (!confirmToggle) return;
		setSaving(true);
		try {
			await MemberLibraryServices.update(confirmToggle.id, { is_active: !confirmToggle.isActive });
			NotistackServices.showSuccessMessage(confirmToggle.isActive ? "Template desativado." : "Template ativado!");
			setConfirmToggle(null);
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao alterar status.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.pageHeader}>
					<div>
						<h2>Biblioteca de Membros</h2>
						<p>Gerencie os templates de membros e limites de tokens por senioridade.</p>
					</div>
					<div className={styles.headerActions}>
						<button
							className={clsx(styles.btn, styles.btnSecondary)}
							onClick={load}
							disabled={loading}
						>
							<Icon
								path={mdiRefresh}
								size={0.7}
							/>
							Recarregar
						</button>
					</div>
				</div>

				<div className={styles.filtersToolbar}>
					<div className={styles.searchGroup}>
						<Icon
							path={mdiMagnify}
							size={0.65}
							className={styles.searchIcon}
						/>
						<input
							className={styles.searchInput}
							placeholder="Buscar por nome, slug..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							disabled={loading}
						/>
					</div>
					<div className={styles.templateCount}>
						<span>
							{filtered.length} template{filtered.length !== 1 && "s"}
						</span>
					</div>
				</div>

				<div className={styles.content}>
					{loading ? (
						<div className={styles.loadingState}>
							<CircularProgress
								color="inherit"
								size={24}
							/>
							<span>Carregando templates...</span>
						</div>
					) : filtered.length === 0 ? (
						<div className={styles.emptyState}>
							<Icon
								path={mdiRobotOutline}
								size={2}
							/>
							<p>Nenhum template encontrado.</p>
						</div>
					) : (
						<div className={styles.grid}>
							{filtered.map((t) => (
								<div
									key={t.id}
									className={clsx(styles.card, !t.isActive && styles.inactive)}
								>
									<div className={styles.cardHeader}>
										<div className={styles.cardIcon}>
											<Icon
												path={mdiRobotOutline}
												size={1}
											/>
										</div>
										<div className={styles.cardInfo}>
											<h3>{t.name}</h3>
											<span className={styles.slug}>{t.slug}</span>
										</div>
										<div className={styles.cardActions}>
											<Tooltip title="Editar limites de tokens">
												<IconButton
													size="small"
													onClick={() => openEdit(t)}
												>
													<Icon
														path={mdiPencil}
														size={0.7}
													/>
												</IconButton>
											</Tooltip>
											<Tooltip title={t.isActive ? "Desativar" : "Ativar"}>
												<IconButton
													size="small"
													onClick={() => setConfirmToggle(t)}
												>
													<Icon
														path={t.isActive ? mdiTrashCanOutline : mdiCheckOutline}
														size={0.7}
													/>
												</IconButton>
											</Tooltip>
										</div>
									</div>

									<div className={styles.cardBody}>
										{t.description && <p className={styles.description}>{t.description}</p>}
										<div className={styles.meta}>
											<Chip
												label={t.modelConfig.provider}
												size="small"
												variant="outlined"
											/>
											<Chip
												label={t.modelConfig.name}
												size="small"
												variant="outlined"
											/>
											{!t.isActive && (
												<Chip
													label="Inativo"
													size="small"
													color="error"
													variant="outlined"
												/>
											)}
										</div>
										<div className={styles.seniorityRow}>
											{SENIORITY_LEVELS.map((level) => (
												<div
													key={level}
													className={styles.seniorityItem}
												>
													<span className={styles.seniorityLevel}>{level}</span>
													<span className={styles.seniorityTokens}>{formatTokens(t.seniorityConfig?.[level]?.token_limit ?? 0)}</span>
													<span className={styles.seniorityUnit}>tokens</span>
													{t.seniorityConfig?.[level]?.rate_limit_per_minute != null && (
														<span className={styles.seniorityRateLimit}>{t.seniorityConfig[level].rate_limit_per_minute}/min</span>
													)}
												</div>
											))}
										</div>
									</div>

									<div className={styles.cardFooter}>
										<span>Criado: {new Date(t.createdAt).toLocaleDateString("pt-BR")}</span>
										<span>Atualizado: {new Date(t.updatedAt).toLocaleDateString("pt-BR")}</span>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Edit Seniority Dialog */}
			<Dialog
				open={!!editTarget}
				onClose={() => setEditTarget(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ className: styles.dialogPaper }}
			>
				<DialogTitle className={styles.dialogTitle}>
					<span>Limites de Tokens</span>
					<IconButton
						size="small"
						onClick={() => setEditTarget(null)}
					>
						<Icon
							path={mdiClose}
							size={0.8}
						/>
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<p className={styles.dialogHint}>
						Configure o limite máximo de tokens por nível para <strong>{editTarget?.name}</strong>.
					</p>
					<div className={styles.seniorityForm}>
						{SENIORITY_LEVELS.map((level) => (
							<div
								key={level}
								className={styles.seniorityLevelBlock}
							>
								<div className={styles.seniorityLevelTitle}>{level.charAt(0).toUpperCase() + level.slice(1)}</div>
								<div className={styles.seniorityFormRow}>
									<label className={styles.seniorityFormLabel}>Tokens/ciclo</label>
									<TextField
										type="number"
										size="small"
										value={seniorityForm[level].token_limit}
										onChange={(e) =>
											setSeniorityForm((prev) => ({
												...prev,
												[level]: { ...prev[level], token_limit: Math.max(0, Number(e.target.value)) },
											}))
										}
										inputProps={{ min: 0, step: 10_000 }}
										fullWidth
										variant="outlined"
									/>
								</div>
								<div className={styles.seniorityFormRow}>
									<label className={styles.seniorityFormLabel}>Req/minuto</label>
									<TextField
										type="number"
										size="small"
										value={seniorityForm[level].rate_limit_per_minute}
										onChange={(e) =>
											setSeniorityForm((prev) => ({
												...prev,
												[level]: { ...prev[level], rate_limit_per_minute: Math.max(1, Number(e.target.value)) },
											}))
										}
										inputProps={{ min: 1, step: 10 }}
										fullWidth
										variant="outlined"
									/>
								</div>
							</div>
						))}
					</div>
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button
						onClick={() => setEditTarget(null)}
						disabled={saving}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveSeniority}
						disabled={saving}
					>
						{saving ? (
							<CircularProgress
								size={18}
								color="inherit"
							/>
						) : (
							"Salvar"
						)}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Toggle Active Dialog */}
			<Dialog
				open={!!confirmToggle}
				onClose={() => setConfirmToggle(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ className: styles.dialogPaper }}
			>
				<DialogTitle className={styles.dialogTitle}>
					<span>{confirmToggle?.isActive ? "Desativar" : "Ativar"} Template</span>
					<IconButton
						size="small"
						onClick={() => setConfirmToggle(null)}
					>
						<Icon
							path={mdiClose}
							size={0.8}
						/>
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<p className={styles.confirmText}>
						Tem certeza que deseja {confirmToggle?.isActive ? "desativar" : "ativar"} o template <strong>{confirmToggle?.name}</strong>?
					</p>
					{confirmToggle?.isActive && <p className={styles.confirmHint}>O template ficará invisível nos workspaces enquanto inativo.</p>}
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button
						onClick={() => setConfirmToggle(null)}
						disabled={saving}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						color={confirmToggle?.isActive ? "error" : "success"}
						onClick={handleToggleActive}
						disabled={saving}
					>
						{saving ? (
							<CircularProgress
								size={18}
								color="inherit"
							/>
						) : confirmToggle?.isActive ? (
							"Desativar"
						) : (
							"Ativar"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
